import React, { FC, ReactNode, useState } from "react";

import {
	Cancel as CancelIcon,
	Delete as DeleteIcon,
	Edit as EditIcon,
	Save as SaveIcon,
} from "@mui/icons-material";
import {
	Box,
	IconButton,
	Stack,
	type SxProps,
	type Theme,
} from "@mui/material";

interface ShiftEditableItemCardProps {
	isEditing: boolean;
	onEdit: () => void | Promise<void>;
	onSave: () => void | Promise<void>;
	onCancel: () => void | Promise<void>;
	onDelete: () => void | Promise<void>;
	viewContent: ReactNode;
	editContent: ReactNode;
	isNew?: boolean;
	saving?: boolean;
	sx?: SxProps<Theme>;
}

export const ShiftEditableItemCard: FC<ShiftEditableItemCardProps> = ({
	isEditing,
	onEdit,
	onSave,
	onCancel,
	onDelete,
	viewContent,
	editContent,
	isNew = false,
	saving = false,
	sx,
}) => {
	const [hovered, setHovered] = useState(false);

	const runAction = (action: () => void | Promise<void>) => {
		try {
			const result = action();
			if (result instanceof Promise) {
				void result.catch((error) => {
					console.error("Action failed", error);
				});
			}
		} catch (error) {
			console.error("Action failed", error);
		}
	};

	const baseCardSx: SxProps<Theme> = {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		gap: 2,
		py: 1.75,
		px: 2,
		borderRadius: 1.5,
		border: (theme) =>
			`1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)"}`,
		backgroundColor: (theme) =>
			theme.palette.mode === "dark" ? "rgba(255,255,255,0.03)" : "#fff",
		transition: "all 0.2s ease",
		"&:hover": {
			borderColor: (theme) =>
				theme.palette.mode === "dark"
					? "rgba(255,255,255,0.12)"
					: "rgba(0,0,0,0.1)",
			backgroundColor: (theme) =>
				theme.palette.mode === "dark"
					? "rgba(255,255,255,0.05)"
					: "rgba(0,0,0,0.02)",
		},
	};
	const cardSx: SxProps<Theme> = sx ? [baseCardSx, sx] : baseCardSx;

	return (
		<Box
			onMouseEnter={() => setHovered(true)}
			onMouseLeave={() => setHovered(false)}
			sx={cardSx}
		>
			<Box sx={{ flex: 1, minWidth: 0 }}>
				{isEditing ? editContent : viewContent}
			</Box>
			<Stack direction="row" spacing={0.5} alignItems="center">
				{isEditing ? (
					<>
						<IconButton
							size="small"
							color="primary"
							onClick={() => runAction(onSave)}
							disabled={saving}
							aria-label="Save"
							sx={{
								"&:hover": {
									bgcolor: (t) =>
										t.palette.mode === "dark"
											? "rgba(31,128,255,0.15)"
											: "rgba(31,128,255,0.08)",
								},
							}}
						>
							<SaveIcon fontSize="small" />
						</IconButton>
						<IconButton
							size="small"
							onClick={() => runAction(onCancel)}
							disabled={saving}
							aria-label="Cancel"
							sx={{
								color: "text.secondary",
								"&:hover": { bgcolor: "action.hover" },
							}}
						>
							<CancelIcon fontSize="small" />
						</IconButton>
					</>
				) : (
					<>
						<IconButton
							size="small"
							onClick={() => runAction(onEdit)}
							aria-label="Edit"
							sx={{
								opacity: hovered || isNew ? 1 : 0.6,
								"&:hover": { bgcolor: "action.hover" },
							}}
						>
							<EditIcon fontSize="small" />
						</IconButton>
						{!isNew && (
							<IconButton
								size="small"
								color="error"
								onClick={() => runAction(onDelete)}
								aria-label="Delete"
								sx={{
									opacity: hovered ? 1 : 0.5,
									"&:hover": {
										bgcolor: (t) =>
											t.palette.mode === "dark"
												? "rgba(214,18,2,0.15)"
												: "rgba(214,18,2,0.08)",
									},
								}}
							>
								<DeleteIcon fontSize="small" />
							</IconButton>
						)}
					</>
				)}
			</Stack>
		</Box>
	);
};
